import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { ChevronDown, ChevronUp, Sunrise, Sunset, CheckCircle2, Circle, Flame, History, Plus, X } from "lucide-react";
import { todayISO } from "@/lib/utils";

// ── Types ──────────────────────────────────────────────────────────────────
interface Ritual {
  id: string; employee_id: string; ritual_date: string;
  gratitudes: string[] | null; affirmations: string[] | null;
  morning_completed_at: string | null;
  wins: string[] | null; improvement: string | null;
  evening_completed_at: string | null;
}
interface Priority {
  id: string; employee_id: string; priority_date: string;
  text: string; position: number;
  completed: boolean; completed_at: string | null;
  carried_from_id: string | null;
}

const GRATITUDE_COUNT = 3;
const AFFIRMATION_COUNT = 2;
const WIN_COUNT = 3;
const PRIORITY_MAX = 3;

// DATE-001: was new Date().toISOString().slice(0, 10), which rolls over at
// UTC midnight — 7pm in Chicago. An evening ritual on the 9th saved under the
// 10th, and History then disagreed with the dashboard that had just said the
// 9th. todayISO() in lib/utils builds the date from local components.

// Streak = consecutive days ending today where at least the morning was
// completed. Simple, forgiving definition — evening participation nudges
// but doesn't punish.
function computeStreak(rituals: Ritual[]): number {
  const byDate = Object.fromEntries(rituals.map(r => [r.ritual_date, r]));
  let streak = 0;
  const cursor = new Date();
  for (;;) {
    const key = todayISO(cursor);   // same convention as the saved ritual_date
    const r = byDate[key];
    if (r && r.morning_completed_at) streak++;
    else break;
    cursor.setDate(cursor.getDate() - 1);
    if (streak > 365) break; // safety
  }
  return streak;
}

// ── Card ──────────────────────────────────────────────────────────────────
export function DailyRitualCard() {
  const { employee } = useAuth();
  const today = todayISO();

  const [ritual, setRitual] = useState<Ritual | null>(null);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [recentRituals, setRecentRituals] = useState<Ritual[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load today's ritual + priorities + last 30 days for streak calc.
  const load = useCallback(async () => {
    if (!employee) return;
    const [{ data: r }, { data: prList }, { data: recent }] = await Promise.all([
      supabase.from("hq_daily_rituals")
        .select("*")
        .eq("employee_id", employee.id)
        .eq("ritual_date", today)
        .maybeSingle(),
      supabase.from("hq_daily_priorities")
        .select("*")
        .eq("employee_id", employee.id)
        .eq("priority_date", today)
        .order("position"),
      supabase.from("hq_daily_rituals")
        .select("ritual_date,morning_completed_at,evening_completed_at")
        .eq("employee_id", employee.id)
        .gte("ritual_date", todayISO(new Date(Date.now() - 60 * 86400000)))
        .order("ritual_date", { ascending: false }),
    ]);
    setRitual((r as Ritual) ?? null);
    let ps = (prList as Priority[]) ?? [];
    // No priorities today? Ask the DB to roll forward yesterday's incompletes.
    if (ps.length === 0) {
      const { data: rolled } = await supabase.rpc("hq_roll_priorities_forward", { p_date: today });
      if (rolled && Number(rolled) > 0) {
        const { data: fresh } = await supabase.from("hq_daily_priorities")
          .select("*")
          .eq("employee_id", employee.id)
          .eq("priority_date", today)
          .order("position");
        ps = (fresh as Priority[]) ?? [];
      }
    }
    setPriorities(ps);
    setRecentRituals((recent as Ritual[]) ?? []);
  }, [employee, today]);

  useEffect(() => { void load(); }, [load]);

  const streak = useMemo(() => computeStreak(recentRituals), [recentRituals]);

  const morningDone = !!ritual?.morning_completed_at;
  const eveningDone = !!ritual?.evening_completed_at;
  // Show evening section after 2pm local, or as soon as morning is done and
  // the user shows up later in the day, or immediately if morning is already
  // done and it's afternoon.
  const now = new Date();
  const eveningVisible = eveningDone || morningDone && (now.getHours() >= 14);

  // Only tuck the card away once the day is truly finished (morning AND
  // evening). Collapsing right after the morning ritual — as it used to,
  // any time before 2pm — hid the day's priorities so they could never be
  // checked off. Keep it open through the day.
  useEffect(() => {
    setCollapsed(morningDone && eveningDone);
  }, [morningDone, eveningDone]);

  async function upsertRitual(patch: Partial<Ritual>) {
    if (!employee) return;
    setBusy(true); setErr(null);
    const payload = {
      employee_id: employee.id,
      ritual_date: today,
      ...(ritual ?? {}),
      ...patch,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from("hq_daily_rituals").upsert(payload, {
      onConflict: "employee_id,ritual_date",
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    await load();
  }

  async function saveMorning(gratitudes: string[], affirmations: string[], priorityTexts: string[]) {
    if (!employee) return;
    setBusy(true); setErr(null);
    // Upsert ritual
    const ritualPayload = {
      employee_id: employee.id,
      ritual_date: today,
      gratitudes,
      affirmations,
      morning_completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const { error: rerr } = await supabase.from("hq_daily_rituals")
      .upsert(ritualPayload, { onConflict: "employee_id,ritual_date" });
    if (rerr) { setBusy(false); setErr(rerr.message); return; }

    // Replace priorities for today
    // First, keep existing rows so we don't lose completion state — sync by index.
    const existing = priorities;
    const trimmed = priorityTexts.map(t => t.trim()).filter(Boolean).slice(0, PRIORITY_MAX);
    // Update existing where possible, insert new, delete extras.
    const ops: Promise<unknown>[] = [];
    for (let i = 0; i < trimmed.length; i++) {
      const text = trimmed[i];
      if (existing[i]) {
        if (existing[i].text !== text || existing[i].position !== i) {
          ops.push(supabase.from("hq_daily_priorities")
            .update({ text, position: i })
            .eq("id", existing[i].id) as unknown as Promise<unknown>);
        }
      } else {
        ops.push(supabase.from("hq_daily_priorities").insert({
          employee_id: employee.id,
          priority_date: today,
          text,
          position: i,
        }) as unknown as Promise<unknown>);
      }
    }
    for (let i = trimmed.length; i < existing.length; i++) {
      ops.push(supabase.from("hq_daily_priorities").delete().eq("id", existing[i].id) as unknown as Promise<unknown>);
    }
    await Promise.all(ops);
    setBusy(false);
    await load();
  }

  async function saveEvening(wins: string[], improvement: string) {
    if (!ritual && !employee) return;
    await upsertRitual({
      wins: wins.map(s => s.trim()).filter(Boolean).slice(0, WIN_COUNT),
      improvement: improvement.trim() || null,
      evening_completed_at: new Date().toISOString(),
    });
  }

  async function togglePriority(p: Priority) {
    const next = !p.completed;
    const nextTs = next ? new Date().toISOString() : null;
    // Optimistic: flip it immediately so the tap feels instant, then persist.
    setPriorities(prev => prev.map(x => x.id === p.id ? { ...x, completed: next, completed_at: nextTs } : x));
    setErr(null);
    const { data, error } = await supabase.from("hq_daily_priorities")
      .update({ completed: next, completed_at: nextTs })
      .eq("id", p.id)
      .select("id");
    if (error || !data || data.length === 0) {
      // Revert on failure and surface it instead of silently doing nothing.
      setPriorities(prev => prev.map(x => x.id === p.id ? { ...x, completed: p.completed, completed_at: p.completed_at } : x));
      setErr(error ? `Couldn't save: ${error.message}` : "Couldn't save that change — please retry.");
    }
  }

  if (!employee) return null;

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-base font-semibold">Daily ritual</h2>
            <div className="text-xs text-gray2">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              {streak > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-orange-500 font-semibold">
                  <Flame size={12} />{streak} day{streak === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/rituals" className="text-xs text-gray2 hover:text-teal-dark inline-flex items-center gap-1" title="History">
            <History size={14} />
          </Link>
          <button onClick={() => setCollapsed(c => !c)} className="text-gray2 hover:text-teal-dark" aria-label="Toggle">
            {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </button>
        </div>
      </div>

      {err && <div className="mt-2 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{err}</div>}

      {collapsed ? (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-3 text-xs text-gray2">
            <span className="inline-flex items-center gap-1"><Sunrise size={12} /> Morning {morningDone ? "✓" : "—"}</span>
            <span className="inline-flex items-center gap-1"><Sunset size={12} /> Evening {eveningDone ? "✓" : "—"}</span>
            {priorities.length > 0 && (
              <span>· {priorities.filter(p => p.completed).length}/{priorities.length} priorities done</span>
            )}
          </div>
          {/* Priorities stay checkable even when the card is collapsed. */}
          {priorities.length > 0 && (
            <ul className="space-y-1">
              {priorities.map(p => (
                <li key={p.id} className="flex items-start gap-2 text-sm">
                  <button onClick={() => void togglePriority(p)} className="shrink-0 mt-0.5 text-teal-dark" aria-label="toggle priority">
                    {p.completed ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                  </button>
                  <span className={`flex-1 ${p.completed ? "line-through text-gray2" : ""}`}>{p.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          <MorningSection
            ritual={ritual}
            priorities={priorities}
            busy={busy}
            onToggle={togglePriority}
            onSave={saveMorning}
          />
          {eveningVisible && (
            <EveningSection ritual={ritual} busy={busy} onSave={saveEvening} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Morning ────────────────────────────────────────────────────────────────
function MorningSection({ ritual, priorities, busy, onToggle, onSave }: {
  ritual: Ritual | null;
  priorities: Priority[];
  busy: boolean;
  onToggle: (p: Priority) => Promise<void>;
  onSave: (g: string[], a: string[], p: string[]) => Promise<void>;
}) {
  const morningDone = !!ritual?.morning_completed_at;

  // Draft state — hydrate from server on load
  const [gratitudes, setGratitudes] = useState<string[]>(() =>
    padArray(ritual?.gratitudes ?? [], GRATITUDE_COUNT));
  const [affirmations, setAffirmations] = useState<string[]>(() =>
    padArray(ritual?.affirmations ?? [], AFFIRMATION_COUNT));
  const [priorityTexts, setPriorityTexts] = useState<string[]>(() =>
    padArray(priorities.map(p => p.text), PRIORITY_MAX));

  useEffect(() => { setGratitudes(padArray(ritual?.gratitudes ?? [], GRATITUDE_COUNT)); }, [ritual?.gratitudes]);
  useEffect(() => { setAffirmations(padArray(ritual?.affirmations ?? [], AFFIRMATION_COUNT)); }, [ritual?.affirmations]);
  useEffect(() => { setPriorityTexts(padArray(priorities.map(p => p.text), PRIORITY_MAX)); }, [priorities.length]);

  const canSave = priorityTexts.some(t => t.trim()) || gratitudes.some(t => t.trim()) || affirmations.some(t => t.trim());

  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-teal-dark">
        <Sunrise size={14} /> Morning intent
        {morningDone && <span className="text-[10px] text-ok normal-case">saved · you can still edit</span>}
      </div>

      <div className="space-y-4">
        {/* Top 3 priorities */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray2 mb-1.5">
            Top {PRIORITY_MAX} priorities today
          </div>
          {morningDone ? (
            <ul className="space-y-1.5">
              {priorities.map(p => (
                <li key={p.id} className="flex items-start gap-2 text-sm group">
                  <button onClick={() => onToggle(p)} className="shrink-0 mt-0.5 text-teal-dark" aria-label="toggle">
                    {p.completed ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                  </button>
                  <span className={`flex-1 ${p.completed ? "line-through text-gray2" : ""}`}>{p.text}</span>
                  {p.carried_from_id && (
                    <span className="text-[10px] rounded-full bg-warn-bg text-warn px-1.5 py-0.5">carried over</span>
                  )}
                </li>
              ))}
              {priorities.length === 0 && <li className="text-sm text-gray2">No priorities set today.</li>}
            </ul>
          ) : (
            <div className="space-y-1.5">
              {priorityTexts.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray2 shrink-0 w-4 text-right">{i + 1}.</span>
                  <input
                    className="input flex-1"
                    placeholder={`Priority #${i + 1}`}
                    value={t}
                    onChange={e => setPriorityTexts(arr => arr.map((v, j) => j === i ? e.target.value : v))}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gratitudes */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray2 mb-1.5">
            3 gratitudes
          </div>
          {morningDone && (ritual?.gratitudes ?? []).length > 0 ? (
            <ul className="space-y-0.5 text-sm text-gray2">
              {(ritual!.gratitudes ?? []).map((g, i) => <li key={i}>• {g}</li>)}
            </ul>
          ) : (
            <div className="space-y-1.5">
              {gratitudes.map((v, i) => (
                <input
                  key={i}
                  className="input"
                  placeholder={`I'm grateful for…`}
                  value={v}
                  onChange={e => setGratitudes(arr => arr.map((x, j) => j === i ? e.target.value : x))}
                />
              ))}
            </div>
          )}
        </div>

        {/* Affirmations */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray2 mb-1.5">
            2 affirmations
          </div>
          {morningDone && (ritual?.affirmations ?? []).length > 0 ? (
            <ul className="space-y-0.5 text-sm text-gray2">
              {(ritual!.affirmations ?? []).map((g, i) => <li key={i}>• {g}</li>)}
            </ul>
          ) : (
            <div className="space-y-1.5">
              {affirmations.map((v, i) => (
                <input
                  key={i}
                  className="input"
                  placeholder="I am… / I will…"
                  value={v}
                  onChange={e => setAffirmations(arr => arr.map((x, j) => j === i ? e.target.value : x))}
                />
              ))}
            </div>
          )}
        </div>

        {!morningDone && (
          <div className="flex justify-end">
            <button
              onClick={() => onSave(gratitudes, affirmations, priorityTexts)}
              className="btn"
              disabled={busy || !canSave}
            >
              {busy ? "Saving…" : "Lock in the day"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Evening ────────────────────────────────────────────────────────────────
function EveningSection({ ritual, busy, onSave }: {
  ritual: Ritual | null;
  busy: boolean;
  onSave: (wins: string[], improvement: string) => Promise<void>;
}) {
  const eveningDone = !!ritual?.evening_completed_at;
  const [wins, setWins] = useState<string[]>(() => padArray(ritual?.wins ?? [], WIN_COUNT));
  const [improvement, setImprovement] = useState(ritual?.improvement ?? "");

  useEffect(() => { setWins(padArray(ritual?.wins ?? [], WIN_COUNT)); }, [ritual?.wins]);
  useEffect(() => { setImprovement(ritual?.improvement ?? ""); }, [ritual?.improvement]);

  const canSave = wins.some(w => w.trim()) || improvement.trim();

  return (
    <section className="border-t border-line pt-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-teal-dark">
        <Sunset size={14} /> End of day reflection
        {eveningDone && <span className="text-[10px] text-ok normal-case">saved · you can still edit</span>}
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray2 mb-1.5">
            3 things that were great today
          </div>
          {eveningDone && (ritual?.wins ?? []).length > 0 ? (
            <ul className="space-y-0.5 text-sm text-gray2">
              {(ritual!.wins ?? []).map((g, i) => <li key={i}>• {g}</li>)}
            </ul>
          ) : (
            <div className="space-y-1.5">
              {wins.map((v, i) => (
                <input
                  key={i}
                  className="input"
                  placeholder="What went great?"
                  value={v}
                  onChange={e => setWins(arr => arr.map((x, j) => j === i ? e.target.value : x))}
                />
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray2 mb-1.5">
            How could tomorrow be better?
          </div>
          {eveningDone && ritual?.improvement ? (
            <p className="text-sm text-gray2">{ritual.improvement}</p>
          ) : (
            <textarea
              className="input min-h-[64px]"
              placeholder="One thing…"
              value={improvement}
              onChange={e => setImprovement(e.target.value)}
            />
          )}
        </div>
        {!eveningDone && (
          <div className="flex justify-end">
            <button
              onClick={() => onSave(wins, improvement)}
              className="btn"
              disabled={busy || !canSave}
            >
              {busy ? "Saving…" : "Close out the day"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────
function padArray(arr: string[], n: number): string[] {
  const out = arr.slice(0, n);
  while (out.length < n) out.push("");
  return out;
}
